import { useEffect } from 'react';
import { useHistory } from 'react-router';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form, notification, Switch } from 'antd';

import { createUploadFileObj } from '@/core/domain/archive/Archive/Archive.utils';

import {
  useActiveOrInactivateConsumable,
  useConsumable,
  useDeleteConsumable,
} from '@/core/domain/operational/Consumables/Consumables.hooks';

import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';

import { useDefaultDeleteModal } from '@/hooks';

import { PageProps } from '@/types/PageProps';

import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import ConsumableForm, {
  ConsumableFormData,
} from '@/components/Operational/Inventory/Consumables/ConsumableForm';
import { DeleteButton, SaveButton } from '@/components/_common/Buttons';
import { useConsumableFormContext } from '../ConsumableProvider';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import ConsumableFooter from '@/components/Operational/Inventory/Consumables/ConsumableFooter';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';

const basePath = '/app/operational/inventory/consumables';

function ConsumableGeneralInfoPage({ route }: PageProps) {
  const intl = useIntl();
  const [form] = Form.useForm<ConsumableFormData>();
  const { setParamLabel } = useBreadcrumbs();
  const { handleDelete } = useDefaultDeleteModal('consumables');
  const history = useHistory();

  const {
    consumableId,
    contextForm,
    disabled,
    isLoading,
    active,
    setActive,
    handleSubmit,
  } = useConsumableFormContext();

  const { data, isFetching } = useConsumable(+consumableId);

  const deleteMutation = useDeleteConsumable();
  const activateOrInactivateMutation = useActiveOrInactivateConsumable();

  useEffect(() => {
    if (contextForm.consumableGeneral) {
      form.setFieldsValue(contextForm.consumableGeneral);
    } else if (data) {
      const { imageUrl, ...rest } = data;

      const img = createUploadFileObj(imageUrl);

      const formObj: ConsumableFormData = {
        ...rest,
        manufacturerId: rest.manufacturer?.id,
        subGroupId: rest.subGroup?.id,
        imageUrl: img ? [img] : [],
      };

      form.setFieldsValue(formObj);

      setParamLabel(':consumableId', data.name);

      setActive(data.active);
    }
  }, [data, isFetching]);

  const handleSaveClick = async () => {
    try {
      await form.validateFields();
      handleSubmit();
    } catch {}
  };

  const handleActiveClick = async (active: boolean) => {
    try {
      activateOrInactivateMutation.mutateAsync(Number(consumableId));

      setActive(active);
    } catch (error) {
      notification.error({
        message: intl.formatMessage({
          id: 'commons.form.notification.error',
        }),
        description: intl.formatMessage({
          id: 'commons.form.notification.errorDescription',
        }),
      });
    }
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: 'consumables.details' })} />

      <DefaultRouteTabs
        route={route.parent}
        baseKey={consumableId}
        basePath={`/app/operational/inventory/consumables/${consumableId}`}
        extra={
          <SpaceForButton>
            <Switch
              unCheckedChildren={intl.formatMessage({
                id: 'commons.entity.inactive',
              })}
              checkedChildren={intl.formatMessage({
                id: 'commons.entity.active',
              })}
              loading={activateOrInactivateMutation.isLoading}
              checked={active}
              onChange={handleActiveClick}
              disabled={!hasPermissions([Authorities.REGISTER_CONSUMABLE_EDIT])}
            />

            <SaveButton
              block
              type="primary"
              onClick={handleSaveClick}
              loading={isLoading}
              disabled={
                disabled ||
                !hasPermissions([Authorities.REGISTER_CONSUMABLE_EDIT])
              }
            >
              <FormattedMessage id="commons.entity.save" />
            </SaveButton>

            <DeleteButton
              disabled={
                !hasPermissions([Authorities.REGISTER_CONSUMABLE_DELETE])
              }
              block
              onClick={() =>
                handleDelete(
                  () => deleteMutation.mutateAsync(Number(consumableId)),
                  async () => history.replace(basePath),
                )
              }
            >
              <FormattedMessage id="commons.entity.remove" />
            </DeleteButton>
          </SpaceForButton>
        }
      >
        <ConsumableForm
          isLoading={isFetching}
          form={form}
          consumableDetail={data}
        />

        <ConsumableFooter disabled={disabled} />
      </DefaultRouteTabs>
    </>
  );
}

export default ConsumableGeneralInfoPage;
